import {Dispatch, useCallback, useContext, useReducer, useState} from 'react';
import {
  GET_CATALOG_ITEM_REPLACEMENT,
  GET_RUN_FOR_ITEM_DETAIL,
  SIMILAR_ITEMS_FOR_DESCRIPTION,
} from '@buncha/config/graphQL/documentNode/queries';
import {
  GetCatalogItemResponse,
  GetRunForItemDetailResponse,
  Item,
  RecommendationCard,
  RecommenderParams,
  RunForItemDetail,
  SimilarItemsForDescriptionResponse,
  State,
} from '../types/ReplacementItemDetails';
import {PayloadAction, createSlice} from '@reduxjs/toolkit';
import {
  ReplacementItemDetailsContext,
  initialState,
} from '../context/ReplacementItemDetails';

import {ApiState} from '@buncha/common/types/common';
import {ToastType} from '@buncha/components/composites/notification/type';
import {gqlService} from '@buncha/config/graphQL';
import {useToastMessage} from '@buncha/components/composites/notification';

const reducer = {
  setItemData: function (state: State, action: PayloadAction<Item>) {
    state.item = action.payload;
  },
  setRunData: function (state: State, action: PayloadAction<RunForItemDetail>) {
    state.runData = action.payload;
  },
  setRunDataLoading: function (state: State, action: PayloadAction<boolean>) {
    state.runDataLoading = action.payload;
  },
  setSimilarItems(state: State, action: PayloadAction<RecommendationCard>) {
    state.similarItemsForDescription = action.payload;
  },
};

const slice = createSlice({
  initialState,
  name: 'ReplacementItemDetailsContext',
  reducers: reducer,
});

export function useReplacementItemDetailsReducer() {
  const reducerInfo = useReducer(slice.reducer, initialState);
  return reducerInfo;
}

export function useReplacementItemDetailsContext() {
  return useContext(ReplacementItemDetailsContext);
}

export const ReplacementItemDetailsAction = slice.actions;

export function useGetRunForItemDetail(
  dispatch: Dispatch<any> | null,
): [(runId: number) => void, ApiState<RunForItemDetail>] {
  const [state, setState] = useState<ApiState<RunForItemDetail>>({
    loading: false,
    error: undefined,
    data: undefined,
  });

  const getRunForItemDetail = useCallback(
    async (runId: number) => {
      if (dispatch)
        dispatch(ReplacementItemDetailsAction.setRunDataLoading(true));
      setState({...state, loading: true});
      try {
        const response = await gqlService?.query<GetRunForItemDetailResponse>({
          query: GET_RUN_FOR_ITEM_DETAIL,
          variables: {
            runId: runId,
          },
          fetchPolicy: 'cache-first',
        });
        if (response?.data?.getRun)
          setState({...state, data: response?.data.getRun, loading: false});
        if (dispatch && response?.data?.getRun) {
          dispatch(
            ReplacementItemDetailsAction.setRunData(response?.data?.getRun),
          );
          dispatch(ReplacementItemDetailsAction.setRunDataLoading(false));
        }
      } catch (error: any) {
        if (dispatch)
          dispatch(ReplacementItemDetailsAction.setRunDataLoading(false));
        setState({...state, loading: false});
      }
    },
    [dispatch, state],
  );

  return [getRunForItemDetail, state];
}

export function useGetCatalogItem(dispatch: Dispatch<any>) {
  const [showErrorToast] = useToastMessage(ToastType.Error);
  const getCatalogItem = (params: {
    itemId: number;
    storeIndex: string;
    itemSource: string;
    itemIndex: number;
    runId?: number;
    searchResultType?: string;
    userQuery?: string;
    maxSearchResultsLength?: number;
  }) => {
    gqlService
      ?.query<GetCatalogItemResponse>({
        query: GET_CATALOG_ITEM_REPLACEMENT,
        variables: params,
        fetchPolicy: 'network-only',
      })
      .then(response => {
        const data = response.data?.getCatalogItem;
        if (data) dispatch(ReplacementItemDetailsAction.setItemData(data));
      })
      .catch((err: any) => {
        showErrorToast(err?.message);
      });
  };

  return {getCatalogItem};
}

export function useSimilarItemsForDescription(
  dispatch: Dispatch<any> | null,
): [(variables: RecommenderParams) => void, ApiState<RecommendationCard>] {
  const [state, setState] = useState<ApiState<RecommendationCard>>({
    loading: false,
    error: undefined,
    data: undefined,
  });
  const func = useCallback(
    async (variables: RecommenderParams) => {
      try {
        setState({...state, loading: true});
        const result =
          await gqlService?.query<SimilarItemsForDescriptionResponse>({
            query: SIMILAR_ITEMS_FOR_DESCRIPTION,
            variables: {
              ...variables,
            },
            fetchPolicy: 'network-only',
          });
        const data = result?.data?.similarItemsForDescription;
        if (data && dispatch) {
          setState({...state, loading: false, data});
          dispatch(ReplacementItemDetailsAction.setSimilarItems(data));
        }
      } catch (err: any) {
        setState({...state, loading: false, data: undefined, error: err});
      }
    },
    [dispatch, state],
  );
  return [func, state];
}
